import { UndoPromptContent, UndoPromptContentProps } from "@components/_Misc";
import { captureException } from "@sentry/minimal";
import { storageKeys } from "@src/constants";
import dayjs from "dayjs";
import type { FormEvent } from "react";
import { ReactNotificationOptions, store } from "react-notifications-component";
import { Path } from "../typings/backend/library";
import { getCachedAuthInfo } from "@services/backend-api";

/** https://stackoverflow.com/a/12646864 */
export function shuffleArray(array: Array<unknown>) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
}

export type TTextFieldObj<T, UpdatableKeys extends keyof T> = {
  State: Pick<T, UpdatableKeys>;
  TextFields: {
    key: keyof Pick<T, UpdatableKeys>;
    name: string;
    placeholder: string;
    multiline?: boolean;
  }[];
};

export function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  const R = [];
  for (let i = 0, len = array.length; i < len; i += chunkSize) R.push(array.slice(i, i + chunkSize));
  return R;
}

/**
 * @param v optionally set the return string if value is plural (default is 's', 'es' might be needed depending on the word)
 * @param x optionally set the return string if value is singular (default is "")
 */
export function plur(i: number, v = "s", x = "") {
  if (i > 1 || i === 0) return v;
  return x;
}
// @ts-ignore
window.setGglAccIndex = (index: number) => {
  if (!(index > 0)) return console.log("invalid number");
  setCookie("G_AUTHUSER_H", `${index}`, 365);
  console.log("done - a reload may be required");
};
export function setGglAccIndex(index: number) {
  if (!(index > 0)) return console.log("invalid number");
  setCookie("G_AUTHUSER_H", `${index}`, 365);
  console.log("done - a reload may be required");
}

let accIndex: number;
export function getGoogleAuthUserIndex(): number {
  try {
    if (typeof accIndex !== "number") {
      const match = document.cookie.match(/G_AUTHUSER_H=\d+/);
      const n = match ? parseInt(match[0].split("=")[1]) : 0;
      accIndex = n;
    }
    return accIndex;
  } catch (error) {
    captureException(error);
    accIndex = 0;
    return 0;
  }
}

const _base = `https://res.cloudinary.com/evie/image`;
export const buildCloudinaryUrl = (params: string, path: string) => `${_base}/upload/${params}/v1/${path}`;
export const buildCloudinaryFetchUrl = (remote_url: string) => `${_base}/fetch/${remote_url}`;

// https://res.cloudinary.com/evie/image/fetch/https://www.example.com//s//wp-content//uploads//MV5BNjVlNjIwYmUtNWQ3OC00NjE2LWIwYjctY2E3NDIwOGViOTdlXkEyXkFqcGdeQXVyMDM2NDM2MQ@@._V1_.jpg

/**
 * Returns one of: local path (if starts with /),
 * cloudinary path (if starts with weepy/) OR
 * the original "str" value (string | null | undefined)
 */
export function buildAssetUrl(str?: string | null, cloudinary_params?: string) {
  if (!str) return undefined;
  const url = str.startsWith("/")
    ? `${import.meta.env.VITE_BASE_URL}${str}`
    : str.startsWith("weepy/") || str.startsWith("undefined/")
    ? buildCloudinaryUrl(cloudinary_params || "", str)
    : str;
  return url;
}

type Image = { cloudinary_path: string };
type MaybeImage = Image | null;
export function buildImageUrl(maybeImage: MaybeImage, cloudinary_params?: string) {
  if (!maybeImage?.cloudinary_path) return undefined;
  return buildAssetUrl(maybeImage.cloudinary_path, cloudinary_params);
}

type DisplayMessageOpts = Omit<ReactNotificationOptions, "type" | "message" | "container"> & {
  removeId?: string;
  container?: ReactNotificationOptions["container"];
};
export const displayMessage = (
  msg: string,
  type: ReactNotificationOptions["type"] = "success",
  opts: DisplayMessageOpts = { container: "bottom-center" }
) => {
  opts.removeId && store.removeNotification(opts.removeId);
  return store.addNotification({
    message: msg,
    type,
    container: opts.container || "bottom-center",
    width: 150,
    dismiss: {
      duration: type === "danger" || type === "warning" ? 3500 : 2500,
      // waitForAnimation: true,
      click: false,
      pauseOnHover: true,
      // onScreen: true,
    },
    ...opts,
  });
};

let last_undo_notification_id = "";
export const displayUndo = (props: UndoPromptContentProps) => {
  last_undo_notification_id && store.removeNotification(last_undo_notification_id);
  last_undo_notification_id = store.addNotification({
    container: "bottom-center",
    content: <UndoPromptContent {...props} />,
    dismiss: { duration: 6000 },
  });
};

export const captureAndDisplayError = (error: Error, display = true) => {
  console.error(error);
  display && displayMessage(error?.message || "Unknown Error", "danger");
  captureException(error);
};

export const displayError = (err: Error) => {
  console.error(err);
  store.addNotification({
    message: err?.message || "Unknown Error",
    type: "danger",
    container: "top-right",
    // width: 150,
    dismiss: { duration: 3500 },
  });
};

export function fixPaths(item_root: string, paths: Path[], isSingle: boolean) {
  // TODO: put this in the backend
  return paths.map((itemPath) => {
    const seperator = itemPath.path.includes("\\") ? "\\" : "/";
    const pathSplit = itemPath.path.split(seperator);
    const rootIndex = pathSplit.findIndex((s) => s === item_root);
    const i = isSingle ? 0 : 1;
    return {
      id: itemPath.id,
      path: pathSplit.slice(rootIndex + i),
      created_at: itemPath.created_at,
      drive_id: itemPath.drive_id,
    };
  });
}

export function toDateInt(str: string | number | null) {
  if (!str) return null;
  if (typeof str === "number") return str;
  return Date.parse(str);
}

export function toDateStr(str: string | number | null) {
  if (!str) return null;
  if (typeof str === "string") return str;
  return new Date(str).toISOString();
}

let email = "";
export function buildGdriveLink(drive_id: string, isFile: boolean) {
  if (!email) {
    email = getCachedAuthInfo().email;
  }
  const link = isFile
    ? `https://drive.google.com/file/d/${drive_id}/view?authuser=${email}`
    : `https://drive.google.com/drive/folders/${drive_id}?authuser=${email}`;
  return link;
}

export function calculateAge(birthday: Date, died?: Date) {
  const start = died ? dayjs(died) : dayjs();
  return start.diff(dayjs(birthday), "years");
}

export function combineURLs(baseURL: string, relativeURL: string) {
  return relativeURL ? baseURL.replace(/\/+$/, "") + "/" + relativeURL.replace(/^\/+/, "") : baseURL;
}

export async function delay(ms: number) {
  return await new Promise((resolve) => setTimeout(resolve, ms));
}

/** Return the value of "page" param. Default 1 if not set. */
export const getUrlParamPage = (location: Location) => {
  const search = new URLSearchParams(location.search);
  const page = search.get("page") ? parseInt(search.get("page") as string) || 1 : 1;
  return page;
};

export function checkIsTouchDevice() {
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
}

/** Check whether localStorage & sessionStorage is supported. */
export function lsSupported() {
  return typeof Storage !== "undefined" && typeof localStorage !== "undefined" && typeof sessionStorage !== "undefined";
}

// const seperator = p.includes("\\") ? "\\" : "/";
export const getFileSep = (path: string): "\\" | "/" => (path.includes("\\") ? "\\" : "/");
/** returns "file name.ext" from p, basically path.basename() */
export const formatPath = (p: string) => p.split(getFileSep(p)).reverse()[0];

export const isPushApiSupported = () => "serviceWorker" in navigator && "Notification" in window && "PushManager" in window;

export const minsToMs = (mins: number) => mins * 60 * 1000;

export const getPushSub = () => getCookie(storageKeys.pushsub);
export const getCookie = (name: string) => {
  return document.cookie
    .split(";")
    .find((c) => c.trim().startsWith(name + "="))
    ?.replace(`${name}=`, "")
    .trim();
};

export const setCookie = (name: string, value: string, days: number) => {
  var expires = "";
  if (days) {
    var date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + (value || "") + expires + "; path=/";
};

export const deleteCookie = (name: string, path: string, domain: string) => {
  if (getCookie(name)) {
    document.cookie =
      name +
      "=" +
      (path ? ";path=" + path : "") +
      (domain ? ";domain=" + domain : "") +
      ";expires=Thu, 01 Jan 1970 00:00:01 GMT";
  }
};

export const withApostropheS = (str: string) => `${str}${str.toLowerCase().endsWith("s") ? `'` : `'s`}`;

// can also do: obj1: Record<keyof T, unknown>, obj2: Record<keyof T, unknown> - to enforce obj with same keys
/**
 * returns true if a key in obj1 (arg0) is different from the same key in obj2
 */
export const compareKeys = <T extends Record<string, unknown>>(obj1: T, obj2: T): boolean => {
  for (const key in obj1) {
    if (obj1[key] !== obj2[key]) return true;
  }
  return false;
};

export function isInViewport(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

// export const getExternalHitHandlers = (href: string) => {
//   const fn = () => [
//     // sendExternalHit(href);
//     // onSearchResultClicked();
//   ];
//   return {
//     onClick: fn,
//     onAuxClick: fn,
//   };
// };

export function preventDefaultAndExec(e: FormEvent, fn: () => unknown) {
  e.preventDefault();
  return fn();
}

export function formatSeasonEpisode(ep: { season_number: number; episode_number: number; title?: string | null }) {
  return `${ep.season_number}x${ep.episode_number}${ep.title ? ` — ${ep.title}` : ""}`;
}

// export const propToString = (str: string) => ``
